import { z } from 'zod';

/**
 * Zod contracts for the "Solicitudes y Mis cotizaciones" (Milestone 5) endpoint family —
 * `src/shared/storage-api.ts`'s wire shapes, written from the REAL backend source, re-confirmed
 * this session against `nexvia-api-web`'s actual handler/repository files (not from this repo's
 * own, previously hand-written `StorageRequestSummary`/`StorageQuote` interfaces, even though those
 * turned out to match — see each schema's own comment for exactly which file was read):
 *
 * - `nexvia-api-web/src/modules/storage/handlers/get-storage-quotes-inbox.handler.ts` — bakes its
 *   own envelope: `return { success: true, data: { requests } }`. `requests` comes from
 *   `storage-request.repository.ts`'s `findPublishedInboxForProfile` (and, for a single lookup,
 *   `toPlain`) — both produce the exact field list below.
 * - `nexvia-api-web/src/modules/storage/handlers/get-storage-quotes-mine.handler.ts` — bakes its
 *   own envelope: `return { success: true, data: { quotes } }`. `quotes` comes from
 *   `storage-quote.repository.ts`'s `findByProfileId`, which is the ONE path that also attaches a
 *   nested `request` summary (department/locality/spaceType/status) — `findByRequestAndProfile`/
 *   `save`'s own `toPlain` never set `request`, confirming the field is genuinely optional, not
 *   just untested.
 * - `nexvia-api-web/src/modules/storage/handlers/create-storage-quote.handler.ts` — bakes its own
 *   envelope: `return { success: true, data: saved }`, `saved` being
 *   `storage-quote.repository.ts`'s `save()` return (its own `toPlain`, no nested `request`).
 *
 * All three handlers' `res.json(result)` controller call is a pass-through — the handler's own
 * `return` statement is the real envelope source here, unlike the `/storage/locations` family
 * where the controller itself adds `{success,data}` around a bare repository return.
 *
 * Field list for `StorageQuote` cross-checked against `storage-quote.entity.ts` (the `storage_quotes`
 * TypeORM EntitySchema, including the fee-snapshot columns added by
 * `1789300000000-AddFeeSnapshotToStorageQuotes.js`) and `storage-quote.repository.ts`'s `toPlain` /
 * `findByProfileId` mappers — `monthlyPrice`/`fullPeriodPrice`/`serviceFeePercent`/
 * `paymentFeePercent`/`ivaPercent`/`monthlyClientAmount`/`fullPeriodClientAmount` are all `Number(...)`
 * coerced server-side before the JSON response leaves the handler, so they arrive as JSON numbers,
 * not numeric strings — modelled as `z.number()`, not `z.coerce.number()`. These fee-engine fields
 * are rendered nowhere in this app yet (root `AGENTS.md`'s danger-zone table, Blocker #1) but the
 * wire contract genuinely carries them, so the schema models them faithfully regardless.
 */

export const SpaceTypeSchema = z.enum(['box_chico', 'box_mediano', 'box_grande', 'personalizado']);
export type SpaceType = z.infer<typeof SpaceTypeSchema>;

// NEX-1165 — decision A9 (plan 2026-10-15): the storage type is the code of a catalog row of kind
// 'storage_type' (`GET /storage/service-options?kind=storage_type`; seeded privado, compartido and
// otro, Nexvia can add more of up to 50 characters), no longer the fixed privado/compartido CHECK.
export const StorageTypeSchema = z.string().min(1);
export type StorageType = z.infer<typeof StorageTypeSchema>;

export const RequestStatusSchema = z.enum(['published', 'accepted', 'cancelled', 'expired']);
export type RequestStatus = z.infer<typeof RequestStatusSchema>;

export const QuoteStatusSchema = z.enum(['pending', 'accepted', 'rejected', 'expired', 'cancelled']);
export type QuoteStatus = z.infer<typeof QuoteStatusSchema>;

/**
 * NEX-1170 — the guardado's lifecycle (`storage_requests.storage_status`, nexvia-api-web
 * `storage.constants.ts` `STORAGE_STATUS`): `null` until a payment accepts a quote, then
 * contracted (accepted and paid) → active (reception confirmed, decision A4) → ending (the client
 * asked to cancel; runs until `endsAt`) → ended. The request's own `status` stays `accepted`.
 */
export const StorageStatusSchema = z.enum(['contracted', 'active', 'ending', 'ended']);
export type StorageStatus = z.infer<typeof StorageStatusSchema>;

/**
 * NEX-1170 — who confirmed the reception: the depósito from this panel, or Nexvia support by hand.
 * NEX-1168 (plan 2026-10-20 Tarea 2.5) — or the carrier, entering the delivery code of a shipment
 * sent straight to the depósito (`storage_requests.reception_confirmed_by = 'delivery_code'`): without
 * it here, one such guardado would fail all of Mis cotizaciones with CONTRACT_VIOLATION.
 */
export const ReceptionConfirmedBySchema = z.enum(['storage', 'backoffice', 'delivery_code']);

/**
 * NEX-1170 (X1) — who ended the guardado (nexvia-api-web `storage.constants.ts` `ENDED_BY`): the
 * client ("Retiré por mi cuenta"), this depósito ("El cliente retiró sus cosas") or the API's daily
 * job, once a cancelled guardado's `endsAt` passed.
 */
export const EndedBySchema = z.enum(['client', 'storage', 'system']);
export type EndedBy = z.infer<typeof EndedBySchema>;

/**
 * NEX-1170 — the lifecycle fields both `GET /storage/quotes/mine` (under each quote's `request`) and
 * the depósito's reception endpoints send. Instants (`receivedAt`, `notReceivedAt`,
 * `cancelRequestedAt`, `endedAt`, `withdrawalRequestedAt`) are ISO timestamps; `endsAt` is a
 * calendar day "YYYY-MM-DD" (read as `::text` server-side). `notReceivedReason` is the depósito's own
 * note to operations ("No lo recibí"), sent to the depósito and support only.
 *
 * [ASSUMED] deploy order (same as the review keys below and the backoffice's NEX-1170 fields): an
 * ABSENT key (an API before NEX-1170) reads as `null` — no guardado yet — instead of failing all of
 * Mis cotizaciones with CONTRACT_VIOLATION; a present value of the wrong type still fails.
 */
const GuardadoLifecycleShape = {
  storageStatus: StorageStatusSchema.nullable().default(null),
  receivedAt: z.string().nullable().default(null),
  receptionNote: z.string().nullable().default(null),
  receptionConfirmedBy: ReceptionConfirmedBySchema.nullable().default(null),
  notReceivedAt: z.string().nullable().default(null),
  notReceivedReason: z.string().nullable().default(null),
  endsAt: z.string().nullable().default(null),
  cancelRequestedAt: z.string().nullable().default(null),
  endedAt: z.string().nullable().default(null),
  // NEX-1170 (X1) — absent before X1 reads as `null` (unknown), same tolerance as the rest.
  endedBy: EndedBySchema.nullable().default(null),
  withdrawalRequestedAt: z.string().nullable().default(null)
};

/**
 * NEX-1329 (decision A6) — one side's review of a guardado, as the parties see it: nexvia-api-web
 * `domain/guardado-review.ts`'s `reviewView`. `rating` is a whole 1–5 (`storage.schemas.ts`
 * `submitStorageReviewBody`); `comment` is trimmed server-side, `null` when empty.
 */
export const StorageReviewSchema = z.object({
  id: z.number(),
  rating: z.number().int().min(1).max(5),
  comment: z.string().nullable(),
  createdAt: z.string()
});
export type StorageReview = z.infer<typeof StorageReviewSchema>;

/**
 * `GET /storage/quotes/inbox` item — `storage-request.repository.ts`'s
 * `findPublishedInboxForProfile` row mapping (mirrors its own `toPlain`, field for field, plus the
 * one field only this join adds: `clientName`). `clientName` is "Andrés M." — first name in full,
 * last name cut to its initial — formatted server-side by `domain/client-display-name.ts`'s
 * `formatClientDisplayName` from `clients.firstName`/`lastName` (falling back to splitting
 * `users.name`). `null` only when neither source has anything at all; the key itself is always
 * present, never omitted, so `.nullable()` and not `.optional()`.
 */
export const StorageRequestSummarySchema = z.object({
  id: z.number(),
  clientId: z.number(),
  clientName: z.string().nullable(),
  spaceType: SpaceTypeSchema,
  volumeM3: z.number().nullable(),
  storageType: StorageTypeSchema,
  department: z.string(),
  locality: z.string(),
  periodStart: z.string(),
  periodMonths: z.number().nullable(),
  openEnded: z.boolean(),
  photos: z.array(z.string()),
  neededOptions: z.array(z.string()),
  needsTransport: z.boolean(),
  // NEX-1166 / audit S2 — who quotes the transfer the client needs (nexvia-api-web
  // `storage-request.repository.ts`'s `findPublishedInboxForProfile`): `true` "cotizarlo con
  // Nexvia", `false` "lo resuelvo yo" (also `false` when `needsTransport` is false).
  // [ASSUMED] deploy order: optional, so an API that does not send it yet reads as unknown instead of
  // failing the whole inbox with CONTRACT_VIOLATION; a present value of the wrong type still fails.
  transportQuotedByNexvia: z.boolean().nullable().optional(),
  status: RequestStatusSchema,
  createdAt: z.string(),
  updatedAt: z.string()
});
export type StorageRequestSummary = z.infer<typeof StorageRequestSummarySchema>;

/**
 * The nested request summary `GET /storage/quotes/mine` attaches to each quote —
 * `storage-quote.repository.ts`'s `findByProfileId` own `request: { department, locality,
 * spaceType, status, clientName }` object, read off its hand-written SQL join's aliased columns.
 * `clientName` is the exact same "Andrés M." / `null` shape as `StorageRequestSummarySchema`'s own
 * field above — both routes go through the one shared `formatClientDisplayName` (see
 * `domain/client-display-name.ts` on the `nexvia-api-web` side) so the inbox and "mine" listings can
 * never format the same client two different ways.
 */
export const StorageQuoteRequestSummarySchema = z.object({
  department: z.string(),
  locality: z.string(),
  spaceType: SpaceTypeSchema,
  status: RequestStatusSchema,
  clientName: z.string().nullable(),
  // NEX-1170 — the guardado's lifecycle (`storage-quote.repository.ts`'s `guardadoOf`): the keys
  // are always sent, with the request's values only on the quote the client contracted and `null`
  // on every other one (that request's guardado, if any, is another depósito's).
  ...GuardadoLifecycleShape,
  // NEX-1329 (decision A6) — the depósito's side of the guardado's reviews
  // (`get-storage-quotes-mine.handler.ts`, `domain/guardado-review.ts`'s `reviewStateFor("storage")`):
  // `canReview` from the reception until the depósito reviews the client, `myReview` what the
  // depósito wrote about the client, `theirReview` what the client wrote about the depósito. Always
  // sent; `false`/`null` on every quote but the accepted one.
  // [ASSUMED] deploy order (as the backoffice's NEX-1170 fields): an ABSENT key (an API before
  // NEX-1329) defaults to nothing to review, instead of failing the whole list; a wrong type still fails.
  canReview: z.boolean().default(false),
  myReview: StorageReviewSchema.nullable().default(null),
  theirReview: StorageReviewSchema.nullable().default(null)
});
export type StorageQuoteRequestSummary = z.infer<typeof StorageQuoteRequestSummarySchema>;

/**
 * Audit S4 — `storage-quote.repository.ts`'s `toQuoteLocation`: the depósito's location a quote
 * names. Its `photos` are not modeled (stripped): this panel only names the address.
 */
const StorageQuoteLocationSchema = z.object({
  id: z.number(),
  address: z.string(),
  department: z.string(),
  locality: z.string()
});

/**
 * NEX-1168 (plan 2026-10-20 Tarea 2.5/2.7) — the shipment the client sent straight to this depósito
 * ("Cotizar el traslado": the depósito is the shipment's destination) for the guardado of the
 * accepted quote, while it runs (`get-storage-quotes-mine.handler.ts`). `deliveryCode` is the code the
 * carrier enters when it hands the things over, which confirms the reception by itself; `null` once
 * the code is no longer active (it expired), and the depósito confirms the reception from here.
 * `status` is the shipment's own status, read as text (not modeled: nothing here branches on it).
 */
const LinkedShipmentSchema = z.object({
  id: z.number(),
  status: z.string(),
  deliveryCode: z.string().nullable().default(null)
});

/**
 * A `storage_quotes` row as the API hands it back — `storage-quote.repository.ts`'s `toPlain` /
 * `findByProfileId` mapping, cross-checked against `storage-quote.entity.ts`'s column list.
 * `request` is attached only on `GET /storage/quotes/mine` (`findByProfileId`) — `save()`'s own
 * `toPlain` (used by `POST /storage/requests/:id/quotes`'s response) never sets it, so it stays
 * `.optional()`, not `.nullable()` (the key is genuinely absent, not present-and-null).
 */
export const StorageQuoteSchema = z.object({
  id: z.number(),
  storageRequestId: z.number(),
  storageProfileId: z.number(),
  storageLocationId: z.number().nullable(),
  monthlyPrice: z.number(),
  fullPeriodPrice: z.number(),
  currency: z.string(),
  includedServices: z.array(z.string()),
  conditions: z.string().nullable(),
  transportIncluded: z.boolean(),
  validUntil: z.string().nullable(),
  status: QuoteStatusSchema,
  serviceFeePercent: z.number(),
  paymentFeePercent: z.number(),
  ivaPercent: z.number(),
  monthlyClientAmount: z.number(),
  fullPeriodClientAmount: z.number().nullable(),
  createdAt: z.string(),
  acceptedAt: z.string().nullable(),
  request: StorageQuoteRequestSummarySchema.optional(),
  // NEX-1165 (W2-hard-a) — `storage-quote.repository.ts`'s list mappers (`findByProfileId`,
  // `findByRequestId`): each `includedServices` value with its catalog label, same order, inactive
  // catalog rows included; a value with no catalog row (free text) is its own label. Absent on
  // save()'s own row (the POST response), hence optional.
  includedServiceLabels: z.array(z.object({ code: z.string(), label: z.string() })).optional(),
  // Audit S4 — the location the quote was sent from (`storage-quote.repository.ts`'s
  // `toQuoteLocation`, on the list mappers): `null` for a quote sent before NEX-1165 made it
  // required or whose location was deleted since; absent on save()'s own row (the POST response).
  location: StorageQuoteLocationSchema.nullable().optional(),
  // NEX-1168 — on the accepted quote with a shipment linked to its guardado; `null` on every other.
  // [ASSUMED] deploy order: an ABSENT key (an API before NEX-1168, or save()'s own row) reads as
  // `null` instead of failing the list; a present value of the wrong type still fails.
  linkedShipment: LinkedShipmentSchema.nullable().default(null)
});
export type StorageQuote = z.infer<typeof StorageQuoteSchema>;

/** `GET /storage/quotes/inbox`'s own baked-in envelope (the handler's `return`, not the controller). */
export const StorageQuotesInboxResponseSchema = z.object({
  success: z.literal(true),
  data: z.object({ requests: z.array(StorageRequestSummarySchema) })
});

/** `GET /storage/quotes/mine`'s own baked-in envelope. */
export const StorageQuotesMineResponseSchema = z.object({
  success: z.literal(true),
  data: z.object({ quotes: z.array(StorageQuoteSchema) })
});

/** `POST /storage/requests/:id/quotes`'s own baked-in envelope — `data` is the saved quote itself. */
export const CreateStorageQuoteResponseSchema = z.object({
  success: z.literal(true),
  data: StorageQuoteSchema
});

/**
 * NEX-1170 (decision A4) — the guardado as `POST /storage/requests/:id/confirm-reception`,
 * `.../report-not-received` and (X1) `.../confirm-withdrawal` answer it: nexvia-api-web `domain/guardado-view.ts`'s `guardadoView`
 * with `withNotReceivedReason` (`handlers/depot-reception.handler.ts`). `paymentModality` is how
 * the accepted quote was paid; the depósito's handler does not pass it, so it comes back `null`
 * there, but the view always carries the key.
 */
export const StorageGuardadoSchema = z.object({
  id: z.number(),
  status: RequestStatusSchema,
  paymentModality: z.enum(['one_time', 'subscription']).nullable(),
  withdrawalDraftId: z.number().nullable(),
  ...GuardadoLifecycleShape
});
export type StorageGuardado = z.infer<typeof StorageGuardadoSchema>;

/** The reception endpoints' envelope: the handler returns `{ success, data: { request } }`, passed through. */
export const StorageGuardadoResponseSchema = z.object({
  success: z.literal(true),
  data: z.object({ request: StorageGuardadoSchema })
});

/**
 * NEX-1329 — `POST /storage/requests/:id/reviews`'s envelope (`guardado-review.handler.ts`'s
 * `submit`, passed through): 201 `{ success, data: { review } }`, the review just saved plus the
 * side it was written from (from this panel, always `storage_to_client`).
 */
export const SubmitStorageReviewResponseSchema = z.object({
  success: z.literal(true),
  data: z.object({
    review: StorageReviewSchema.extend({ reviewType: z.enum(['client_to_storage', 'storage_to_client']) })
  })
});
export type SubmittedStorageReview = z.infer<typeof SubmitStorageReviewResponseSchema>['data']['review'];
